import { useState } from 'react';
import { SYMPTOM_LABEL } from '@heartcoach/care';
import type { AppSession, PatientView, Symptom } from '../../lib/types';
import { ApiError, api } from '../../lib/api';
import { Icon } from '../../components/HeartMark';

/** The two we never wait on. Kept in step with the care package's own urgent list. */
const URGENT: readonly Symptom[] = ['chest-tightness', 'shortness-of-breath'];

/**
 * Reporting how you feel, with no blood pressure reading in the way.
 *
 * Until this existed the Symptoms button opened the BP flow, which refuses to go anywhere without a
 * valid systolic and diastolic: a patient with chest tightness and no cuff to hand could not tell
 * anyone. The symptoms offered come from the patient's care track, so a heart-failure patient is
 * asked about swelling and sleeping flat, and someone with fibrillation about their rhythm.
 */
export function SymptomFlow({ session, view, onDone, onClose }: { session: AppSession; view: PatientView; onDone: () => Promise<void>; onClose: () => void }) {
  const [picked, setPicked] = useState<Symptom[]>([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ insight: string | null; urgent: boolean } | null>(null);

  const options = view.track.symptoms;
  const toggle = (s: Symptom) => setPicked(picked.includes(s) ? picked.filter((x) => x !== s) : [...picked, s]);
  const urgent = picked.some((s) => URGENT.includes(s));

  const submit = async () => {
    if (picked.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.postEvent({ type: 'log-symptoms', symptoms: picked, ...(note.trim() ? { note: note.trim() } : {}) }, session.token);
      setDone({ insight: r.insight, urgent });
      await onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not send that. Try again, and if this is urgent call your clinic.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label="Report symptoms">
      <div className="modal">
        <header className="modal-head">
          <h2>{done ? 'Reported' : 'How are you feeling?'}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            <Icon name="close" size={18} />
          </button>
        </header>

        <div className="modal-body">
          <p className="emergency">
            <Icon name="alert" size={16} /> Chest pain, trouble breathing or fainting: call 911 now. Do not wait for an answer here.
          </p>

          {done ? (
            <>
              <p>{done.insight}</p>
              <p className="hint">
                {done.urgent
                  ? `This has been flagged to ${view.doctor.name} and the team at ${view.practiceName}.`
                  : `Noted for ${view.doctor.name} and the team at ${view.practiceName}. They read messages during clinic hours.`}
              </p>
            </>
          ) : (
            <>
              <span className="eyebrow">What are you noticing?</span>
              <div className="chips">
                {options.map((s) => (
                  <button key={s} type="button" className={`chip ${picked.includes(s) ? 'chip-on chip-red' : ''}`} aria-pressed={picked.includes(s)} onClick={() => toggle(s)}>
                    {SYMPTOM_LABEL[s]}
                  </button>
                ))}
              </div>
              <label className="field">
                <span>Anything else worth telling them? (optional)</span>
                <textarea value={note} rows={3} maxLength={500} placeholder="When it started, what you were doing, how long it lasted." onChange={(e) => setNote(e.target.value)} />
              </label>
              {error ? <p className="form-error">{error}</p> : null}
            </>
          )}
        </div>

        <footer className="modal-foot">
          {done ? (
            <button type="button" className="primary" onClick={onClose}>
              Back to home
            </button>
          ) : (
            <button type="button" className="primary big" disabled={busy || picked.length === 0} onClick={() => void submit()}>
              {busy ? 'Sending…' : urgent ? 'Tell my care team now' : 'Tell my care team'}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
